import React, { useState, useEffect, useCallback } from 'react';
import ScGrid from '../ScGrid';
import ScModal from '../ScModal';
import ScSpinner from '../ScSpinner';
import { fetchEmailInfo, saveEmailInfo } from '../../studyConfigurationApi';
import { detectPresetLabel, presetToForm } from '../../emailGatewayPresets';

const emptyForm = () => ({
  smsGatewayNo: 0,
  smtpPortNo: 1,
  smsUrl: '',
  smsUser: '',
  smsPwd: '',
  smsSender: '',
  smsResponse: 'Y',
  fromEmail: '',
  password: '',
  tenantId: '',
  clientId: '',
  secretKey: '',
  authSecretKey: false,
  isActive: true,
  recordedAtTimeZone: null,
  replicaFlag: null,
  internationalSmsUrl: '',
  internationalSmsAuthKey: '',
  presetKey: null,
  presetLabel: null,
  clearSecretKey: false,
});

const toForm = (row) => ({
  ...row,
  password: '',
  secretKey: '',
  smsPwd: '',
  smtpPortNo: row.smtpPortNo ?? 1,
  smsResponse: row.smsResponse || 'Y',
  authSecretKey: !!row.authSecretKey,
  isActive: row.isActive !== false,
  presetKey: null,
  presetLabel: null,
  clearSecretKey: false,
});

const truncate = (value, max = 36) => {
  if (value == null || value === '') return '—';
  const s = String(value);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const maskedLabel = (masked) => (masked ? '••••••••' : '—');

const GATEWAY_COLUMNS = [
  {
    key: 'configuration',
    label: 'Configuration',
    render: (r) => detectPresetLabel(r.fromEmail),
  },
  { key: 'fromEmail', label: 'From Email' },
  { key: 'tenantId', label: 'Tenant ID', render: (r) => truncate(r.tenantId, 28) },
  { key: 'clientId', label: 'Client ID', render: (r) => truncate(r.clientId, 28) },
  { key: 'smsUser', label: 'SMS User' },
  { key: 'smsSender', label: 'SMS Sender' },
  { key: 'smsUrl', label: 'SMS URL', render: (r) => truncate(r.smsUrl, 32) },
  {
    key: 'password',
    label: 'Password',
    render: (r) => maskedLabel(r.passwordMasked),
  },
  {
    key: 'secretKey',
    label: 'Secret Key',
    render: (r) => maskedLabel(r.secretKeyMasked),
  },
  { key: 'isActive', label: 'Active', render: (r) => (r.isActive ? 'Yes' : 'No') },
];

const FormField = ({
  label,
  children,
  fullWidth,
  hint,
}) => (
  <div className={`sc-form-row${fullWidth ? ' sc-form-row-full' : ''}`}>
    <label>{label}</label>
    {children}
    {hint && <span className="sc-form-hint">{hint}</span>}
  </div>
);

const GatewayForm = ({ form, set, manualEdit, isAddMode }) => {
  if (!form) return null;
  const setField = (patch) => set((prev) => ({ ...prev, ...patch }));
  const keepSecretsHint = manualEdit;

  return (
    <div className="sc-email-gateway-form sc-email-gateway-form-modal">
      {form.presetKey ? (
        <p className="sc-form-hint sc-email-gateway-hint">
          {`Review the ${form.presetLabel} preset values below, then click Save.`}
        </p>
      ) : isAddMode ? (
        <p className="sc-form-hint sc-email-gateway-hint">
          Click Sarjen or LTR to load a preset, or enter values manually.
        </p>
      ) : keepSecretsHint ? (
        <p className="sc-form-hint sc-email-gateway-hint">
          Leave password, secret key, or SMS password blank to keep existing values.
        </p>
      ) : null}
      <FormField label="From Email" fullWidth>
        <input value={form.fromEmail || ''} onChange={(e) => setField({ fromEmail: e.target.value })} />
      </FormField>
      <FormField label="Password" hint={keepSecretsHint && form.passwordMasked ? 'Leave blank to keep' : undefined}>
        <input
          type="password"
          placeholder={keepSecretsHint && form.passwordMasked ? 'Leave blank to keep' : ''}
          value={form.password || ''}
          onChange={(e) => setField({ password: e.target.value })}
        />
      </FormField>
      <FormField label="Tenant ID">
        <input value={form.tenantId || ''} onChange={(e) => setField({ tenantId: e.target.value })} />
      </FormField>
      <FormField label="Client ID">
        <input value={form.clientId || ''} onChange={(e) => setField({ clientId: e.target.value })} />
      </FormField>
      <FormField label="Secret Key" hint={keepSecretsHint && form.secretKeyMasked ? 'Leave blank to keep' : undefined}>
        <input
          type="password"
          placeholder={keepSecretsHint && form.secretKeyMasked ? 'Leave blank to keep' : ''}
          value={form.secretKey || ''}
          onChange={(e) => setField({ secretKey: e.target.value })}
        />
      </FormField>
      <FormField label="SMS URL" fullWidth>
        <input value={form.smsUrl || ''} onChange={(e) => setField({ smsUrl: e.target.value })} />
      </FormField>
      <FormField label="SMS User">
        <input value={form.smsUser || ''} onChange={(e) => setField({ smsUser: e.target.value })} />
      </FormField>
      <FormField label="SMS Password" hint={keepSecretsHint && form.smsPwdMasked ? 'Leave blank to keep' : undefined}>
        <input
          type="password"
          placeholder={keepSecretsHint && form.smsPwdMasked ? 'Leave blank to keep' : ''}
          value={form.smsPwd || ''}
          onChange={(e) => setField({ smsPwd: e.target.value })}
        />
      </FormField>
      <FormField label="SMS Sender">
        <input value={form.smsSender || ''} onChange={(e) => setField({ smsSender: e.target.value })} />
      </FormField>
      <FormField label="International SMS URL" fullWidth>
        <input
          value={form.internationalSmsUrl || ''}
          onChange={(e) => setField({ internationalSmsUrl: e.target.value })}
        />
      </FormField>
      <FormField label="International SMS Auth Key" fullWidth>
        <input
          value={form.internationalSmsAuthKey || ''}
          onChange={(e) => setField({ internationalSmsAuthKey: e.target.value })}
        />
      </FormField>
    </div>
  );
};

const EmailGatewayTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchEmailInfo();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load email credential.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setModalMode('add');
    setEditId(null);
    setError(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const applyPreset = (presetKey) => {
    setForm(presetToForm(presetKey, rows[0]));
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setEditId(row.smsGatewayNo);
    setError(null);
    setForm(toForm(row));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMode(null);
    setForm(emptyForm());
    setEditId(null);
  };

  const buildPayload = () => {
    const isPresetSave = !!form.presetKey;
    const base = {
      smsGatewayNo: 1,
      smtpPortNo: Number(form.smtpPortNo) > 0 ? Number(form.smtpPortNo) : 1,
      smsUrl: form.smsUrl,
      smsUser: form.smsUser,
      smsSender: form.smsSender,
      smsResponse: form.smsResponse || 'Y',
      fromEmail: form.fromEmail,
      tenantId: form.tenantId,
      clientId: form.clientId,
      authSecretKey: form.authSecretKey,
      isActive: form.isActive,
      recordedAtTimeZone: form.recordedAtTimeZone == null || form.recordedAtTimeZone === ''
        ? null
        : Number(form.recordedAtTimeZone),
      replicaFlag: form.replicaFlag || null,
      internationalSmsUrl: form.internationalSmsUrl,
      internationalSmsAuthKey: form.internationalSmsAuthKey,
      overwriteSecrets: isPresetSave,
      clearSecretKey: !!form.clearSecretKey,
    };

    if (isPresetSave) {
      return {
        ...base,
        smsPwd: form.smsPwd ?? '',
        password: form.password ?? '',
        secretKey: form.secretKey ?? '',
      };
    }

    return {
      ...base,
      smsPwd: form.smsPwd || undefined,
      password: form.password || undefined,
      secretKey: form.secretKey || undefined,
      clearSecretKey: false,
    };
  };

  const save = async () => {
    setError(null);
    setSuccess(null);
    try {
      await saveEmailInfo(buildPayload());
      const label = form.presetLabel || 'Email credential';
      setSuccess(`${label} saved.`);
      closeModal();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    }
  };

  const modalTitle = () => (modalMode === 'edit' ? 'Edit Email Credential' : 'Add Email Credential');

  if (loading) return <ScSpinner />;

  const gridRows = rows.map((r) => ({ ...r, id: r.smsGatewayNo }));
  const manualEdit = modalMode === 'edit';
  const showPresetButtons = modalMode === 'add';

  return (
    <div className="sc-email-gateway">
      {error && !modalOpen && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}

      <div className="sc-toolbar">
        <button type="button" className="sc-btn sc-btn-primary" onClick={openAdd}>
          Add Credential
        </button>
      </div>

      <ScGrid
        columns={GATEWAY_COLUMNS}
        rows={gridRows}
        onEdit={openEdit}
        emptyMessage="No email credential configured. Click Add Credential to create one."
      />

      <ScModal
        open={modalOpen}
        size="lg"
        title={modalTitle()}
        onClose={closeModal}
        footer={(
          <>
            <button type="button" className="sc-btn sc-btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="button" className="sc-btn sc-btn-primary" onClick={save}>Save</button>
          </>
        )}
      >
        {error && <div className="sc-alert sc-alert-error">{error}</div>}
        {showPresetButtons && (
          <div className="sc-email-gateway-preset-bar">
            <span className="sc-email-gateway-preset-label">Load preset</span>
            <button
              type="button"
              className="sc-btn sc-btn-secondary"
              onClick={() => applyPreset('sarjen')}
            >
              Sarjen
            </button>
            <button
              type="button"
              className="sc-btn sc-btn-secondary"
              onClick={() => applyPreset('ltr')}
            >
              LTR
            </button>
          </div>
        )}
        <GatewayForm form={form} set={setForm} manualEdit={manualEdit} isAddMode={modalMode === 'add'} />
      </ScModal>
    </div>
  );
};

export default EmailGatewayTab;
